import { TILE_REGISTRY, type TileType } from '../schema/index.js';
import type { DataState } from './states.js';

/**
 * TTL-by-tile-type table + stale/error freshness semantics (H1).
 *
 * ADR-001 §3: TTLs are the shared-cache refresh floors — weather 15m, quotes 60s
 * delayed, news 10m, sports 60s. These are DERIVED from the tile-type registry's
 * `ttlSecFloor` (single source of truth — ADR-002 §3.1) so the cache TTL and the
 * schema's refresh floor can never disagree. The cache stores each value for its
 * type's TTL; cost then scales with distinct (type, provider, params) tuples, not
 * users (D-007).
 */

/** Canonical cache TTL (seconds) per tile type, derived from the registry. */
export const TTL_BY_TILE_TYPE: Readonly<Record<TileType, number>> = Object.freeze(
  Object.fromEntries(
    (Object.keys(TILE_REGISTRY) as TileType[]).map((t) => [t, TILE_REGISTRY[t].ttlSecFloor]),
  ) as Record<TileType, number>,
);

/** TTL for a tile type in seconds (0 = no-fetch, e.g. clock). Total over TileType. */
export function ttlSecFor(type: TileType): number {
  return TTL_BY_TILE_TYPE[type];
}

/**
 * Freshness multipliers (glance-grammar-spec §6.3):
 *  - data is `stale` once age > 2× the tile's refresh interval,
 *  - and escalates to `error` once age > 4× (or 8s of initial loading — §6.1).
 * The cache and the client both use these so "stale" means the same thing on
 * both sides of the wire.
 */
export const STALE_AFTER_MULTIPLIER = 2 as const;
export const ERROR_AFTER_MULTIPLIER = 4 as const;

/** Milliseconds a tile may sit in `loading` before escalating to `error` (§6.1). */
export const LOADING_TIMEOUT_MS = 8_000 as const;

/**
 * Classify freshness from timestamps. Pure & total — the single definition of
 * "fresh vs stale vs error" used by the cache (to set the envelope `state`) and
 * by the client (to decide the freshness badge). A no-fetch type (ttlSec 0) is
 * always `ok`.
 *
 * @param fetchedAt epoch ms the data was fetched upstream
 * @param ttlSec    the tile type's TTL in seconds
 * @param now       epoch ms "now" (injected — no ambient clock, testable)
 */
export function classifyFreshness(
  fetchedAt: number,
  ttlSec: number,
  now: number,
): Extract<DataState, 'ok' | 'stale' | 'error'> {
  if (ttlSec <= 0) return 'ok';
  const ageSec = (now - fetchedAt) / 1000;
  if (ageSec > ttlSec * ERROR_AFTER_MULTIPLIER) return 'error';
  if (ageSec > ttlSec * STALE_AFTER_MULTIPLIER) return 'stale';
  return 'ok';
}
